// Card stepper for product cards.
//
// W1b: the + is OPTIMISTIC. A tap bumps the card chip and the header cart bubble
// synchronously and dispatches POST /cart/add.js in the SAME task — no 300ms debounce and
// no pre-flight GET /cart.js (that serial pair was the measured ~1.9s add). Reconcile runs
// only after the write lands, and syncWithCart is skipped while a card still has work
// outstanding, so a slow response can never undo a newer tap or double-count a sheet add.
//
// Perf marks for QC: 'bg-add-tap' / 'bg-add-post', plus the measures 'bg-add-paint-delta'
// (tap -> chip painted) and 'bg-add-post-delta' (tap -> POST dispatched). Both target <100ms.

// One /cart.js for the whole page at first paint, shared by every card. Previously each
// element fetched its own, so a full grid queued 20+ identical requests behind each other.
window.bgCartOnce = function (force) {
  if (force || !window.__bgCartOnceP) {
    window.__bgCartOnceP = fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json());
  }
  return window.__bgCartOnceP;
};

// Global function to update all product cards on the page
window.updateAllProductCards = function(cartData, skipElement = null) {
  if (!cartData || !cartData.items) return;

  document.querySelectorAll('product-form-inline').forEach((formElement) => {
    try {
      if (skipElement && formElement === skipElement) return;
      if (formElement.syncWithCart) {
        formElement.syncWithCart(cartData);
      }
    } catch (e) {
      console.error('Error updating product card:', e);
    }
  });
};

if (!customElements.get('product-form-inline')) {
    customElements.define(
      'product-form-inline',
      class ProductFormInline extends HTMLElement {
        constructor() {
          super();

          this.form = this.querySelector('form');
          this.form.querySelector('[name=id]').disabled = false;
          this.plusButtons = this.form.querySelectorAll('.plus-button');
          this.minusButtons = this.form.querySelectorAll('.minus-button');
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
          this.plusButtons.forEach(button => button.addEventListener('click', this.onPlusButtonClick.bind(this)));
          this.minusButtons.forEach(button => button.addEventListener('click', this.onMinusButtonClick.bind(this)));
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          this.buttonContainer = this.querySelector('.gc-button-container');

          // Optimistic state.
          this.localQuantity = 0;    // what the shopper is looking at right now
          this.pendingDelta = 0;     // taps not yet sent to /cart
          this.inFlight = false;     // our own /cart write is running
          this.externalPending = 0;  // the pack sheet bumped us and owns that reconcile
          this.touched = false;      // shopper has used this stepper at least once

          /* overlay removed: in-cart shown as corner stepper */

          // Subscribe to cart updates
          try {
            this.cartUpdateUnsubscriber = subscribe && subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
              if (event && event.originElement === this) return;
              if (event && event.cartData) {
                this.syncWithCart(event.cartData);
              }
            });
          } catch (_) {}

          this.submitButton = this.querySelector('[type="submit"]');
          if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
          this.hideErrors = this.dataset.hideErrors === 'true';

          // Seed from the server-rendered count first (free), reconcile off the critical path.
          this.seedFromDom();
          this.initializeFromCart();
        }

        mark(name) { try { if (window.performance && performance.mark) performance.mark(name); } catch (_) {} }
        measure(name, from) { try { performance.measure(name, from); } catch (_) {} }

        // The card is rendered with its in-cart count already in .gc-qty, so first paint
        // needs no network at all.
        seedFromDom() {
          try {
            const wrap = this.closest('.product-card-wrapper') || this.closest('.card-wrapper');
            const qtyEl = wrap && wrap.querySelector('.gc-qty');
            const n = qtyEl ? parseInt((qtyEl.textContent || '').replace(/[^0-9]/g, ''), 10) : 0;
            this.localQuantity = isNaN(n) ? 0 : n;
          } catch (_) {
            this.localQuantity = 0;
          }
        }

        // Reconcile the seed once, shared page-wide and scheduled at idle so it never
        // competes with the first tap.
        initializeFromCart() {
          const go = () => {
            window.bgCartOnce()
              .then((cart) => { if (!this.touched) this.syncWithCart(cart); })
              .catch((e) => { console.error('Failed to initialize cart:', e); });
          };
          if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 1500 });
          else setTimeout(go, 300);
        }

        // True while this card is showing an optimistic value the server has not confirmed.
        isBusy() {
          return this.inFlight || this.pendingDelta !== 0 || this.externalPending > 0;
        }

        // Sync local state with cart data
        syncWithCart(cartData) {
          if (!cartData || !cartData.items) return;
          if (this.isBusy()) return;   // a stale snapshot must never undo a newer tap

          const items = cartData.items || [];

          const productWrapper = this.closest('.product-card-wrapper') || this.closest('.card-wrapper');
          const productId = productWrapper?.getAttribute('data-product-id');

          // Calculate total for this product
          const totalForProduct = items
            .filter((i) => String(i.product_id) === String(productId))
            .reduce((sum, i) => sum + i.quantity, 0);

          // Update local state
          this.localQuantity = totalForProduct;

          // Update display
          this.updateDisplay();
        }

        onPlusButtonClick(evt) {
          evt.preventDefault();
          this.mark('bg-add-tap');

          // Everything the shopper sees moves in this task — chip first, then header bubble.
          this.touched = true;
          this.localQuantity++;
          this.updateDisplay();
          this.measure('bg-add-paint-delta', 'bg-add-tap');
          if (window.bgBumpCartBadge) window.bgBumpCartBadge(1);

          // Idle card => this call reaches fetch() synchronously, still inside the click task.
          this.queueDelta(1);
          this.mark('bg-add-post');
          this.measure('bg-add-post-delta', 'bg-add-tap');
        }

        onMinusButtonClick(evt) {
          evt.preventDefault();

          if (this.localQuantity <= 0) return;

          this.touched = true;
          this.localQuantity--;
          this.updateDisplay();
          if (window.bgBumpCartBadge) window.bgBumpCartBadge(-1);

          this.queueDelta(-1);
        }

        // Optimistic bump owned by an outside adder (snippets/bg-pack-sheet.liquid). Returns
        // a settle function that adder MUST call when its /cart write lands: until it does,
        // this card stays "busy" and refuses reconciles, which is what stops a sheet add and
        // a reconcile both landing on the same counter.
        bgExternalAdd(n) {
          const delta = n || 0;
          this.touched = true;
          this.localQuantity = Math.max(0, this.localQuantity + delta);
          this.updateDisplay();
          this.externalPending++;
          let done = false;
          return (ok) => {
            if (done) return;
            done = true;
            this.externalPending = Math.max(0, this.externalPending - 1);
            if (ok === false) {
              this.localQuantity = Math.max(0, this.localQuantity - delta);
              this.updateDisplay();
            }
          };
        }

        // Coalescing queue: fire straight away when idle, otherwise ride the write already
        // running. No debounce — a single tap must not wait on a timer.
        queueDelta(d) {
          this.pendingDelta += d;
          if (!this.inFlight) this.flush();
        }

        flush() {
          const delta = this.pendingDelta;
          this.pendingDelta = 0;
          if (!delta) return;

          this.inFlight = true;
          const settle = (ok) => {
            this.inFlight = false;
            if (ok === false) this.rollback(delta);
            if (this.pendingDelta !== 0) this.flush();
          };

          if (delta > 0) this.sendAdd(delta, settle);
          else this.sendRemove(-delta, settle);
        }

        // Put back what we optimistically showed, and say so.
        rollback(delta) {
          this.localQuantity = Math.max(0, this.localQuantity - delta);
          this.updateDisplay();
          if (window.bgBumpCartBadge) window.bgBumpCartBadge(-delta);
          const msg = delta > 0 ? "Couldn't add — try again" : "Couldn't update — try again";
          if (window.bgToast) window.bgToast(msg);
        }

        // Add: POST immediately. bg-cart-fix repaints the bubble + drawer from the server and
        // re-syncs every card afterwards; settle() runs first so this card is no longer busy
        // by the time that reconcile arrives.
        sendAdd(quantity, settle) {
          const variantId = this.form.querySelector('.product-variant-id')?.value;
          if (window.bgCartAdd) {
            window.bgCartAdd(variantId, quantity, settle);
            return;
          }
          // bg-cart-fix not loaded (defensive) — plain POST, then a manual re-sync.
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', quantity);
          fetch(`${routes.cart_add_url}`, {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData
          })
            .then((r) => { if (!r.ok) throw new Error('add'); return r.json(); })
            .then(() => {
              settle(true);
              return window.bgCartOnce(true).then((cart) => {
                if (window.updateAllProductCards) window.updateAllProductCards(cart);
              });
            })
            .catch(() => settle(false));
        }

        // Remove needs the line's absolute quantity, so this path — and only this path —
        // reads the cart first. Removes are rare and are not on the add latency budget.
        async sendRemove(quantity, settle) {
          const variantId = this.form.querySelector('.product-variant-id')?.value;
          try {
            const cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
            const variantQty = (cart.items || [])
              .filter((i) => String(i.variant_id) === String(variantId))
              .reduce((sum, i) => sum + i.quantity, 0);

            const body = JSON.stringify({
              id: variantId,
              quantity: Math.max(0, variantQty - quantity),
              sections: this.getSectionsParamForChange(),
              sections_url: window.location.pathname
            });

            const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } });
            const parsed = JSON.parse(await response.text());

            settle(true);

            try {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form-inline',
                cartData: parsed,
                variantId: variantId,
                originElement: this
              });
            } catch (_) {}

            this.updateCartSectionsSilently(parsed);
            if (window.updateAllProductCards) window.updateAllProductCards(parsed);
          } catch (e) {
            console.error('Cart remove failed', e);
            settle(false);
          }
        }

        // Update display based on local state
        updateDisplay() {
          try {
            const productWrapper = this.closest('.product-card-wrapper') || this.closest('.card-wrapper');
            if (!productWrapper) return;
            const minusBtn = productWrapper.querySelector('.minus-button');
            const plusBtn = productWrapper.querySelector('.plus-button');
            const buttonContainer = productWrapper.querySelector('.gc-button-container');
            let qtyEl = productWrapper.querySelector('.gc-qty');
            if (!qtyEl && buttonContainer && plusBtn) {
              qtyEl = document.createElement('span');
              qtyEl.className = 'gc-qty';
              buttonContainer.insertBefore(qtyEl, plusBtn);
            }
            const quantity = this.localQuantity;
            if (quantity > 0) {
              if (buttonContainer) buttonContainer.classList.add('in-cart');
              if (minusBtn) minusBtn.style.display = 'inline-flex';
              if (qtyEl) { qtyEl.style.display = 'inline-flex'; qtyEl.textContent = quantity; qtyEl.classList.remove('bounce'); void qtyEl.offsetWidth; qtyEl.classList.add('bounce'); }
            } else {
              if (buttonContainer) buttonContainer.classList.remove('in-cart');
              if (minusBtn) minusBtn.style.display = 'none';
              if (qtyEl) qtyEl.style.display = 'none';
            }
          } catch (e) {
            console.error('Display update error:', e);
          }
        }

        onSubmitHandler(evt) {
          // Not used in new strategy
          evt.preventDefault();
        }

        handleErrorMessage(errorMessage = false) {
          if (this.hideErrors) return;
          this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
          if (!this.errorMessageWrapper) return;
          this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
          this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
          if (errorMessage) {
            this.errorMessage.textContent = errorMessage;
          }
        }

        updateCartSectionsSilently(parsedState) {
          try {
            const sectionsToRender = (this.cart && this.cart.getSectionsToRender ? this.cart.getSectionsToRender() : [
              { id: 'cart-icon-bubble' }
            ]);
            sectionsToRender.forEach((section) => {
              const container = section.selector
                ? (document.querySelector(section.selector) || document.getElementById(section.id))
                : document.getElementById(section.id);
              if (!container || !parsedState.sections) return;
              const keyId = parsedState.sections[section.id];
              const keySection = section.section ? parsedState.sections[section.section] : undefined;
              const htmlStr = keyId || keySection;
              if (!htmlStr) return;
              const html = new DOMParser().parseFromString(htmlStr, 'text/html');
              const replacement = html.querySelector(section.selector || '.shopify-section') || html.body;
              if (replacement) container.innerHTML = replacement.innerHTML;
            });
          } catch (e) {
            console.error('Silent cart section update failed', e);
          }
        }

        attachOverlayClose(overlayEl) {
          try {
            overlayEl.addEventListener('click', (evt) => {
              const content = overlayEl.querySelector('.overlay-content');
              if (!content || !content.contains(evt.target)) {
                overlayEl.style.display = 'none';
              }
            });
          } catch (_) {}
        }

        getSectionsParamForChange() {
          const sections = [];
          const mainCartItems = document.getElementById('main-cart-items');
          const mainCartFooter = document.getElementById('main-cart-footer');
          if (mainCartItems && mainCartItems.dataset.id) sections.push(mainCartItems.dataset.id);
          if (mainCartFooter && mainCartFooter.dataset.id) sections.push(mainCartFooter.dataset.id);
          sections.push('cart-icon-bubble');
          sections.push('cart-live-region-text');
          return sections;
        }
      }
    );
  }

